import type { ChatBlockContext } from "./block-disclosure";
import { UnknownBlockPlugin, type ChatBlockPlugin } from "./chat-block-plugin";
import type { ChatContentBlock } from "../model/chat-types";

/** Retains the exact renderer that owns each mount, even after a theme swap. */
export class ChatBlockMounts {
  constructor(private reportError: (error: unknown) => void = error => console.error(error)) {}

  private entries: { element: HTMLElement; plugin: ChatBlockPlugin }[] = [];
  private fallback: ChatBlockPlugin = new UnknownBlockPlugin();

  private report(error: unknown): void {
    try { this.reportError(error); } catch { /* diagnostics must not abort rendering */ }
  }

  mount(plugin: ChatBlockPlugin, block: ChatContentBlock, streaming: boolean, context?: ChatBlockContext): HTMLElement {
    let element: HTMLElement | undefined;
    try {
      element = plugin.makeBlock();
      plugin.bindBlock(element, block, streaming, context);
    } catch (error) {
      this.report(error);
      if (element) {
        try { plugin.onRecycle(element); } catch (cleanupError) { this.report(cleanupError); }
        try { element.remove(); } catch (cleanupError) { this.report(cleanupError); }
      }
      // Preserve sibling mounts and their block indices. The failed renderer
      // owns only its failed element; canonical content remains inspectable.
      plugin = this.fallback;
      element = plugin.makeBlock();
      plugin.bindBlock(element, block, streaming, context);
    }
    this.entries.push({ element, plugin });
    return element;
  }

  appendDelta(index: number, delta: string): void {
    const entry = this.entries[index];
    if (entry) entry.plugin.appendDelta(entry.element, delta);
  }

  finalize(index: number): void {
    const entry = this.entries[index];
    if (entry) entry.plugin.finalizeBlock(entry.element);
  }

  clear(): void {
    const entries = this.entries;
    this.entries = [];
    const errors: unknown[] = [];
    for (const { element, plugin } of entries) {
      try { plugin.onRecycle(element); } catch (error) { errors.push(error); }
      try { element.remove(); } catch (error) { errors.push(error); }
    }
    for (const error of errors) {
      // A faulty extension must not abort the virtualizer's remaining drain.
      this.report(error);
    }
  }
}
